import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { aboutData, seoTextData } from "@/mocks/aboutData";

const breadcrumbItems = [
  { label: "首页", path: "/" },
  { label: "关于我们" },
];

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于开云体育",
  description: "开云体育是全球领先的在线体育娱乐平台，致力于为广大体育爱好者提供安全、专业、便捷的一站式体育赛事投注服务。",
  mainEntity: {
    "@type": "Organization",
    name: "开云体育",
    url: "https://www.kaiyunsports.com/",
    description: aboutData.intro,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: aboutData.contactInfo.phone,
      contactType: "customer service",
      email: aboutData.contactInfo.email,
      availableLanguage: ["Chinese"],
    },
  },
};

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      setFormMessage("感谢您的留言，我们会尽快与您联系！");
      form.reset();
      return;
    }

    try {
      const response = await fetch("https://form.readdy.ai/submit", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && (parsed as { code?: string }).code === "OK") {
        setFormStatus("success");
        setFormMessage("感谢您的留言，我们会尽快与您联系！");
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string }; message?: string }).meta?.message
          || (parsed as { message?: string }).message
          || responseText;
        setFormStatus("error");
        setFormMessage(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后重试。");
      }
    } catch {
      setFormStatus("error");
      setFormMessage("网络错误，请检查网络后重试。");
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 开云体育 | 品牌介绍与联系方式"
        description="开云体育是全球领先的在线体育娱乐平台，致力于为广大体育爱好者提供安全、专业、便捷的一站式体育赛事投注服务。了解开云体育品牌故事、信誉资质及联系方式。"
        keywords="开云体育,关于开云体育,开云体育品牌,体育平台,开云体育下载"
        canonicalUrl="https://www.kaiyunsports.com/about"
        schemaJson={aboutSchema}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="py-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Hero */}
          <section className="text-center py-12 md:py-16">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于<strong className="text-primary-600">开云体育</strong>
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {aboutData.slogan}
            </p>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
            {aboutData.stats.map((stat) => (
              <div
                key={stat.label}
                className="p-6 rounded-xl bg-background-100 border border-background-200/60 text-center"
              >
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm text-foreground-600">
                  {stat.label}
                </div>
              </div>
            ))}
          </section>

          {/* Brand Story */}
          <section className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {aboutData.intro}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {aboutData.story}
                </p>
              </div>
              <div className="rounded-xl overflow-hidden h-64 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20luxurious%20office%20interior%20with%20warm%20golden%20lighting%2C%20elegant%20corporate%20headquarters%20setting%2C%20minimalist%20design%20with%20sports%20themed%20decorations%2C%20professional%20atmosphere%2C%20clean%20composition%20with%20rich%20warm%20amber%20tones&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="开云体育品牌"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutData.values.map((v) => (
                <div
                  key={v.title}
                  className="p-6 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-300 transition-all duration-300"
                >
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-3">
                    {v.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {v.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Certifications */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
              信誉资质
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutData.certifications.map((cert) => (
                <div
                  key={cert.name}
                  className="flex flex-col items-center text-center p-6 rounded-xl bg-background-50 border border-background-200/60"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`${cert.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-2">
                    {cert.name}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {cert.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* SEO Text */}
          <section className="mb-16">
            <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/60">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                <a href="/">{seoTextData.mainKeyword}</a>
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {seoTextData.description}
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                {seoTextData.relatedKeywords.map((kw) => (
                  <Link
                    key={kw}
                    to="/"
                    className="px-3 py-1 rounded-full bg-background-200 text-xs text-foreground-600 cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {kw}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                      邮箱地址
                    </h4>
                    <a href={`mailto:${aboutData.contactInfo.email}`} className="text-sm text-foreground-600 cursor-pointer hover:text-primary-600 transition-colors">
                      {aboutData.contactInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                      客服热线
                    </h4>
                    <p className="text-sm text-foreground-600">
                      <strong>{aboutData.contactInfo.phone}</strong>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                      官方微信
                    </h4>
                    <p className="text-sm text-foreground-600">{aboutData.contactInfo.wechat}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                      公司地址
                    </h4>
                    <p className="text-sm text-foreground-600">{aboutData.contactInfo.address}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/60">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                  在线留言
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="true">
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                  >
                    提交留言
                  </button>

                  {formStatus !== "idle" && (
                    <div
                      className={`text-sm p-3 rounded-lg ${
                        formStatus === "success"
                          ? "bg-accent-100 text-accent-800"
                          : "bg-primary-100 text-primary-800"
                      }`}
                    >
                      {formMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}